"use client";

import { useState, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import Link from "@/components/LocalizedLink";
import {
  Home,
  Server,
  BarChart2,
  MessageSquare,
  CreditCard,
  Key,
  Menu,
  X,
  ChevronRight,
  CheckCircle2,
  Download,
  Plus,
  ExternalLink,
  Copy,
  RefreshCw,
  Zap,
  ArrowUpRight,
  ArrowDownRight,
  Shield,
  Globe,
  Activity,
  Info,
  type LucideIcon,
} from "lucide-react";
import { SofiaConnectLogo } from "@/components/SofiaConnectLogo";
import { IP_TRANSIT_SLA_PERCENT } from "@/lib/network-metrics";

// ─────────────────────────────────────────────
// Toast system
// ─────────────────────────────────────────────

type Toast = { id: number; message: string };

function useToast() {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const addToast = useCallback((message: string) => {
    const id = Date.now();
    setToasts((prev) => [...prev, { id, message }]);
    setTimeout(() => setToasts((prev) => prev.filter((t) => t.id !== id)), 3500);
  }, []);
  return { toasts, addToast };
}

function ToastContainer({ toasts }: { toasts: Toast[] }) {
  return (
    <div className="fixed bottom-6 right-6 z-[100] flex flex-col gap-2 pointer-events-none">
      <AnimatePresence>
        {toasts.map((t) => (
          <motion.div
            key={t.id}
            initial={{ opacity: 0, y: 16, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 8, scale: 0.96 }}
            transition={{ duration: 0.25 }}
            className="px-4 py-3 rounded-xl text-sm text-text-primary font-medium shadow-xl"
            style={{
              background: "rgba(10,10,14,0.95)",
              border: "1px solid rgba(74,158,245,0.3)",
              backdropFilter: "blur(12px)",
            }}
          >
            {t.message}
          </motion.div>
        ))}
      </AnimatePresence>
    </div>
  );
}

// ─────────────────────────────────────────────
// SVG Traffic Chart (shared component)
// ─────────────────────────────────────────────

function generateTrafficData(points: number, seed = 1) {
  const ingress: number[] = [];
  const egress: number[] = [];
  // Simulate business-hours peaks with some randomness
  for (let i = 0; i < points; i++) {
    const hour = (i / points) * 24;
    // Business hours peak: 8–20h
    const businessFactor =
      hour >= 8 && hour <= 20
        ? 0.5 + 0.5 * Math.sin(((hour - 8) / 12) * Math.PI)
        : 0.1 + 0.05 * Math.sin(hour);
    const noise = (Math.sin(i * seed * 2.3 + seed) * 0.5 + 0.5) * 0.25;
    ingress.push(Math.min(9.8, Math.max(0.3, businessFactor * 8.5 + noise * 2)));
    egress.push(
      Math.min(8.5, Math.max(0.2, businessFactor * 7.2 + noise * 1.8 - 0.3))
    );
  }
  return { ingress, egress };
}

function TrafficChart({
  width = 600,
  height = 180,
  points = 48,
  maxGbps = 10,
  showLabels = true,
  seed = 1,
}: {
  width?: number;
  height?: number;
  points?: number;
  maxGbps?: number;
  showLabels?: boolean;
  seed?: number;
}) {
  const padLeft = showLabels ? 44 : 8;
  const padBottom = showLabels ? 28 : 6;
  const padTop = 10;
  const padRight = 8;
  const chartW = width - padLeft - padRight;
  const chartH = height - padTop - padBottom;

  const { ingress, egress } = generateTrafficData(points, seed);

  const toX = (i: number) => padLeft + (i / (points - 1)) * chartW;
  const toY = (v: number) => padTop + chartH - (v / maxGbps) * chartH;

  const buildPath = (data: number[]) =>
    data
      .map((v, i) => {
        if (i === 0) return `M ${toX(0)} ${toY(v)}`;
        const px = toX(i - 1);
        const py = toY(data[i - 1]);
        const cx = toX(i);
        const cy = toY(v);
        const cpx = px + (cx - px) * 0.5;
        return `C ${cpx} ${py}, ${cpx} ${cy}, ${cx} ${cy}`;
      })
      .join(" ");

  const buildArea = (data: number[]) =>
    `${buildPath(data)} L ${toX(points - 1)} ${toY(0)} L ${toX(0)} ${toY(0)} Z`;

  const xLabels = ["00:00", "04:00", "08:00", "12:00", "16:00", "20:00", "24:00"];
  const yLabels = [0, 2, 4, 6, 8, 10];

  return (
    <svg
      viewBox={`0 0 ${width} ${height}`}
      className="w-full"
      style={{ height: `${height}px` }}
      aria-label="Traffic chart"
    >
      <defs>
        <linearGradient id="ingressGrad" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#4A9EF5" stopOpacity="0.45" />
          <stop offset="100%" stopColor="#4A9EF5" stopOpacity="0.02" />
        </linearGradient>
        <linearGradient id="egressGrad" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#00d4aa" stopOpacity="0.35" />
          <stop offset="100%" stopColor="#00d4aa" stopOpacity="0.02" />
        </linearGradient>
      </defs>

      {/* Grid lines */}
      {yLabels.map((v) => (
        <line
          key={v}
          x1={padLeft}
          y1={toY(v)}
          x2={padLeft + chartW}
          y2={toY(v)}
          stroke="var(--hover-bg)"
          strokeWidth="1"
        />
      ))}

      {/* Area fills */}
      <path d={buildArea(egress)} fill="url(#egressGrad)" />
      <path d={buildArea(ingress)} fill="url(#ingressGrad)" />

      {/* Stroke lines */}
      <path
        d={buildPath(egress)}
        fill="none"
        stroke="#00d4aa"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
        opacity="0.85"
      />
      <path
        d={buildPath(ingress)}
        fill="none"
        stroke="#4A9EF5"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />

      {/* Y axis labels */}
      {showLabels &&
        yLabels.map((v) => (
          <text
            key={v}
            x={padLeft - 6}
            y={toY(v) + 4}
            textAnchor="end"
            fontSize="9"
            fill="var(--text-muted)"
            fontFamily="monospace"
          >
            {v}G
          </text>
        ))}

      {/* X axis labels */}
      {showLabels &&
        xLabels.map((label, i) => (
          <text
            key={label}
            x={padLeft + (i / (xLabels.length - 1)) * chartW}
            y={height - 6}
            textAnchor="middle"
            fontSize="9"
            fill="var(--text-muted)"
            fontFamily="monospace"
          >
            {label}
          </text>
        ))}
    </svg>
  );
}

// Mini sparkline for services
function Sparkline({ data, color }: { data: number[]; color: string }) {
  const w = 80;
  const h = 28;
  const max = Math.max(...data);
  const min = Math.min(...data);
  const range = max - min || 1;
  const pts = data
    .map((v, i) => {
      const x = (i / (data.length - 1)) * w;
      const y = h - ((v - min) / range) * (h - 4) - 2;
      return `${x},${y}`;
    })
    .join(" ");
  return (
    <svg width={w} height={h} viewBox={`0 0 ${w} ${h}`}>
      <polyline
        points={pts}
        fill="none"
        stroke={color}
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
        opacity="0.8"
      />
    </svg>
  );
}

const sparkData = [2.1, 2.8, 3.2, 4.5, 6.1, 7.8, 8.2, 7.1, 6.5, 5.8, 5.2, 4.9];
const sparkData2 = [55, 60, 72, 81, 88, 92, 90, 85, 80, 75, 70, 65];
const sparkData3 = [0.3, 0.4, 0.45, 0.65, 0.85, 0.88, 0.82, 0.75, 0.6, 0.55, 0.5, 0.45];

// ─────────────────────────────────────────────
// Navigation config
// ─────────────────────────────────────────────

type View = "dashboard" | "services" | "traffic" | "tickets" | "billing" | "apikeys";

const NAV_ITEMS: { id: View; label: string; Icon: LucideIcon }[] = [
  { id: "dashboard", label: "Dashboard", Icon: Home },
  { id: "services", label: "Services", Icon: Server },
  { id: "traffic", label: "Traffic Analytics", Icon: BarChart2 },
  { id: "tickets", label: "Support Tickets", Icon: MessageSquare },
  { id: "billing", label: "Billing", Icon: CreditCard },
  { id: "apikeys", label: "API Keys", Icon: Key },
];

// ─────────────────────────────────────────────
// KPI Card
// ─────────────────────────────────────────────

function KpiCard({
  label,
  value,
  sub,
  trend,
  icon: Icon,
  accentColor,
}: {
  label: string;
  value: string;
  sub?: string;
  trend?: "up" | "down" | "neutral";
  icon: LucideIcon;
  accentColor: string;
}) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      className="rounded-2xl p-5 flex flex-col gap-3 relative overflow-hidden"
      style={{
        background: "var(--hover-bg)",
        border: "1px solid var(--card-border)",
        backdropFilter: "blur(8px)",
      }}
    >
      <div
        className="absolute inset-0 opacity-10 rounded-2xl"
        style={{
          background: `radial-gradient(ellipse at top left, ${accentColor}40, transparent 70%)`,
        }}
      />
      <div className="flex items-center justify-between relative z-10">
        <span className="text-xs font-mono tracking-wider uppercase text-text-muted">
          {label}
        </span>
        <div
          className="w-7 h-7 rounded-lg flex items-center justify-center"
          style={{ background: `${accentColor}20` }}
        >
          <Icon className="w-3.5 h-3.5" color={accentColor} />
        </div>
      </div>
      <div className="relative z-10">
        <span className="text-2xl font-bold text-text-primary tracking-tight">{value}</span>
        {sub && (
          <div className="flex items-center gap-1 mt-1">
            {trend === "up" && <ArrowUpRight className="w-3 h-3 text-emerald-400" />}
            {trend === "down" && <ArrowDownRight className="w-3 h-3 text-red-400" />}
            <span className="text-xs text-text-muted">{sub}</span>
          </div>
        )}
      </div>
    </motion.div>
  );
}

// ─────────────────────────────────────────────
// Status dot
// ─────────────────────────────────────────────

function StatusDot({ status }: { status: "active" | "warning" | "critical" }) {
  const colors = {
    active: "#22c55e",
    warning: "#eab308",
    critical: "#ef4444",
  };
  return (
    <span className="relative flex h-2 w-2">
      <span
        className="animate-ping absolute inline-flex h-full w-full rounded-full opacity-50"
        style={{ background: colors[status] }}
      />
      <span
        className="relative inline-flex rounded-full h-2 w-2"
        style={{ background: colors[status] }}
      />
    </span>
  );
}

// ─────────────────────────────────────────────
// Section card wrapper
// ─────────────────────────────────────────────

function Card({
  children,
  className = "",
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <div
      className={`rounded-2xl p-5 ${className}`}
      style={{
        background: "var(--hover-bg)",
        border: "1px solid var(--card-border)",
        backdropFilter: "blur(8px)",
      }}
    >
      {children}
    </div>
  );
}

function SectionTitle({ children }: { children: React.ReactNode }) {
  return (
    <h2 className="text-sm font-mono tracking-wider uppercase text-text-muted mb-4">
      {children}
    </h2>
  );
}

// ─────────────────────────────────────────────
// DASHBOARD VIEW
// ─────────────────────────────────────────────

const services = [
  {
    name: "IP Transit 10G",
    route: "Sofia ↔ Frankfurt",
    status: "active" as const,
    uptime: IP_TRANSIT_SLA_PERCENT,
  },
  {
    name: "DWDM 100G Wavelength",
    route: "Sofia ↔ Istanbul",
    status: "active" as const,
    uptime: "100%",
  },
  {
    name: "Ethernet L2VPN 1G",
    route: "Sofia ↔ Athens",
    status: "active" as const,
    uptime: "99.97%",
  },
  {
    name: "Dark Fiber Pair",
    route: "Sofia ↔ Tbilisi",
    status: "active" as const,
    uptime: "100%",
  },
];

const recentActivity = [
  {
    event: "Bandwidth upgrade completed: IP Transit 1G → 10G",
    time: "2 hours ago",
    type: "success",
  },
  {
    event: "Ticket #1847 resolved: BGP session timeout",
    time: "1 day ago",
    type: "info",
  },
  {
    event: "Monthly invoice generated: April 2026",
    time: "3 days ago",
    type: "billing",
  },
  {
    event: "New API key created: prod-monitoring-v2",
    time: "1 week ago",
    type: "info",
  },
];

function DashboardView({ onToast }: { onToast: (msg: string) => void }) {
  return (
    <div className="space-y-6">
      {/* KPI row */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <KpiCard
          label="Active Services"
          value="4"
          sub="All operational"
          trend="neutral"
          icon={Server}
          accentColor="#4A9EF5"
        />
        <KpiCard
          label="Network Uptime"
          value={IP_TRANSIT_SLA_PERCENT}
          sub="30-day rolling"
          trend="up"
          icon={Activity}
          accentColor="#22c55e"
        />
        <KpiCard
          label="Open Tickets"
          value="1"
          sub="0 critical"
          trend="neutral"
          icon={MessageSquare}
          accentColor="#eab308"
        />
        <KpiCard
          label="Monthly Spend"
          value="€8,450"
          sub="Simulated data"
          trend="neutral"
          icon={CreditCard}
          accentColor="#4A9EF5"
        />
      </div>

      {/* Two-column section */}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        {/* Left column */}
        <div className="space-y-5">
          {/* Service Status */}
          <Card>
            <SectionTitle>Service Status Overview</SectionTitle>
            <div className="space-y-3">
              {services.map((svc) => (
                <div
                  key={svc.name}
                  className="flex items-center justify-between py-2.5 border-b border-divider last:border-0"
                >
                  <div className="flex items-center gap-3">
                    <StatusDot status={svc.status} />
                    <div>
                      <div className="text-sm font-medium text-text-primary">{svc.name}</div>
                      <div className="text-xs text-text-muted font-mono">{svc.route}</div>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-xs font-mono text-emerald-400">{svc.uptime}</div>
                    <div className="text-xs text-text-muted font-mono">uptime</div>
                  </div>
                </div>
              ))}
            </div>
          </Card>

          {/* Recent Activity */}
          <Card>
            <SectionTitle>Recent Activity</SectionTitle>
            <div className="space-y-3">
              {recentActivity.map((item, i) => (
                <div key={i} className="flex gap-3 items-start">
                  <div
                    className="w-1.5 h-1.5 rounded-full mt-1.5 shrink-0"
                    style={{
                      background:
                        item.type === "success"
                          ? "#22c55e"
                          : item.type === "billing"
                          ? "#4A9EF5"
                          : "#ffffff50",
                    }}
                  />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm text-text-secondary leading-snug">{item.event}</div>
                    <div className="text-xs text-text-muted font-mono mt-0.5">{item.time}</div>
                  </div>
                </div>
              ))}
            </div>
          </Card>
        </div>

        {/* Right column */}
        <div className="space-y-5">
          {/* Traffic Chart */}
          <Card>
            <div className="flex items-center justify-between mb-4">
              <SectionTitle>Traffic Overview</SectionTitle>
              <div className="flex items-center gap-3 text-xs font-mono text-text-muted mb-4">
                <span className="flex items-center gap-1">
                  <span className="w-2 h-0.5 bg-[#4A9EF5] inline-block rounded" />
                  Ingress
                </span>
                <span className="flex items-center gap-1">
                  <span className="w-2 h-0.5 bg-[#00d4aa] inline-block rounded" />
                  Egress
                </span>
              </div>
            </div>
            <TrafficChart width={560} height={180} points={48} seed={7} />
            <div className="mt-2 text-xs text-text-muted font-mono text-center">
              24h · max 10 Gbps · simulated data
            </div>
          </Card>

          {/* Quick Actions */}
          <Card>
            <SectionTitle>Quick Actions</SectionTitle>
            <div className="grid grid-cols-2 gap-3">
              <Link
                href="/#configurator"
                className="flex items-center gap-2 px-4 py-3 rounded-xl text-sm font-medium text-text-secondary transition-all duration-200 hover:text-text-primary group"
                style={{
                  background: "rgba(74,158,245,0.1)",
                  border: "1px solid rgba(74,158,245,0.2)",
                }}
              >
                <Plus className="w-4 h-4 text-[#4A9EF5] group-hover:scale-110 transition-transform" />
                Order New Service
              </Link>
              <button
                onClick={() => onToast("Feature available in production portal")}
                className="flex items-center gap-2 px-4 py-3 rounded-xl text-sm font-medium text-text-secondary transition-all duration-200 hover:text-text-primary group cursor-pointer"
                style={{
                  background: "var(--hover-bg)",
                  border: "1px solid var(--card-border)",
                }}
              >
                <Zap className="w-4 h-4 text-[#eab308] group-hover:scale-110 transition-transform" />
                Upgrade Bandwidth
              </button>
              <Link
                href="/contact"
                className="flex items-center gap-2 px-4 py-3 rounded-xl text-sm font-medium text-text-secondary transition-all duration-200 hover:text-text-primary group"
                style={{
                  background: "var(--hover-bg)",
                  border: "1px solid var(--card-border)",
                }}
              >
                <MessageSquare className="w-4 h-4 text-[#4A9EF5] group-hover:scale-110 transition-transform" />
                Open Ticket
              </Link>
              <button
                onClick={() => onToast("Invoice download available in production portal")}
                className="flex items-center gap-2 px-4 py-3 rounded-xl text-sm font-medium text-text-secondary transition-all duration-200 hover:text-text-primary group cursor-pointer"
                style={{
                  background: "var(--hover-bg)",
                  border: "1px solid var(--card-border)",
                }}
              >
                <Download className="w-4 h-4 text-[#00d4aa] group-hover:scale-110 transition-transform" />
                Download Invoice
              </button>
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────
// SERVICES VIEW
// ─────────────────────────────────────────────

const serviceCards = [
  {
    type: "IP Transit",
    typeColor: "#4A9EF5",
    typeBg: "rgba(74,158,245,0.12)",
    circuitId: "SC-IPT-2847",
    route: "Sofia → Frankfurt",
    bandwidth: "10 Gbps",
    vlan: "VLAN 100",
    sla: "Platinum",
    uptime: IP_TRANSIT_SLA_PERCENT,
    sparkData: sparkData,
    sparkColor: "#4A9EF5",
  },
  {
    type: "DWDM Wavelength",
    typeColor: "#a78bfa",
    typeBg: "rgba(167,139,250,0.12)",
    circuitId: "SC-DWM-1193",
    route: "Sofia → Istanbul",
    bandwidth: "100 Gbps",
    vlan: "Untagged",
    sla: "Diamond",
    uptime: "100%",
    sparkData: sparkData2.map((v) => v / 10),
    sparkColor: "#a78bfa",
  },
  {
    type: "Ethernet L2VPN",
    typeColor: "#00d4aa",
    typeBg: "rgba(0,212,170,0.12)",
    circuitId: "SC-L2V-0554",
    route: "Sofia → Athens",
    bandwidth: "1 Gbps",
    vlan: "VLAN 200",
    sla: "Gold",
    uptime: "99.97%",
    sparkData: sparkData3,
    sparkColor: "#00d4aa",
  },
  {
    type: "Dark Fiber",
    typeColor: "#f97316",
    typeBg: "rgba(249,115,22,0.12)",
    circuitId: "SC-DFP-0088",
    route: "Sofia → Tbilisi",
    bandwidth: "Unmanaged",
    vlan: "Customer managed",
    sla: "Best Effort",
    uptime: "100%",
    sparkData: [1.2, 1.3, 1.1, 1.4, 1.5, 1.3, 1.2, 1.4, 1.3, 1.5, 1.4, 1.3],
    sparkColor: "#f97316",
  },
];

function ServicesView({ onToast }: { onToast: (msg: string) => void }) {
  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between mb-2">
        <h2 className="text-xl font-semibold text-text-primary">Active Services</h2>
        <Link
          href="/#configurator"
          className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium text-[#4A9EF5] transition-colors hover:text-text-primary"
          style={{
            background: "rgba(74,158,245,0.1)",
            border: "1px solid rgba(74,158,245,0.2)",
          }}
        >
          <Plus className="w-3.5 h-3.5" />
          Order New Service
        </Link>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {serviceCards.map((svc) => (
          <motion.div
            key={svc.circuitId}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="rounded-2xl p-5"
            style={{
              background: "var(--hover-bg)",
              border: "1px solid var(--card-border)",
              backdropFilter: "blur(8px)",
            }}
          >
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-center gap-2">
                <span
                  className="text-xs font-mono font-semibold px-2.5 py-1 rounded-lg tracking-wider uppercase"
                  style={{ background: svc.typeBg, color: svc.typeColor }}
                >
                  {svc.type}
                </span>
                <StatusDot status="active" />
              </div>
              <span className="text-xs font-mono text-text-muted">{svc.circuitId}</span>
            </div>

            <div className="mb-4">
              <div className="flex items-center gap-1.5 text-text-secondary font-medium">
                <Globe className="w-3.5 h-3.5 text-text-muted" />
                {svc.route}
              </div>
              <div className="flex gap-4 mt-2">
                <div>
                  <div className="text-xs text-text-muted font-mono uppercase tracking-wider">
                    Bandwidth
                  </div>
                  <div className="text-sm font-mono text-text-secondary mt-0.5">{svc.bandwidth}</div>
                </div>
                <div>
                  <div className="text-xs text-text-muted font-mono uppercase tracking-wider">
                    VLAN
                  </div>
                  <div className="text-sm font-mono text-text-secondary mt-0.5">{svc.vlan}</div>
                </div>
                <div>
                  <div className="text-xs text-text-muted font-mono uppercase tracking-wider">
                    SLA
                  </div>
                  <div className="text-sm font-mono text-text-secondary mt-0.5">{svc.sla}</div>
                </div>
                <div>
                  <div className="text-xs text-text-muted font-mono uppercase tracking-wider">
                    Uptime
                  </div>
                  <div className="text-sm font-mono text-emerald-400 mt-0.5">{svc.uptime}</div>
                </div>
              </div>
            </div>

            <div className="flex items-center justify-between">
              <Sparkline data={svc.sparkData} color={svc.sparkColor} />
              <button
                onClick={() => onToast("Service management available in production portal")}
                className="flex items-center gap-1.5 px-3.5 py-1.5 rounded-lg text-xs font-medium transition-colors cursor-pointer"
                style={{
                  background: "rgba(74,158,245,0.1)",
                  border: "1px solid rgba(74,158,245,0.2)",
                  color: "#4A9EF5",
                }}
              >
                Manage
                <ChevronRight className="w-3 h-3" />
              </button>
            </div>
          </motion.div>
        ))}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────
// TRAFFIC ANALYTICS VIEW
// ─────────────────────────────────────────────

const TIME_RANGES = ["1h", "6h", "24h", "7d", "30d"] as const;
type TimeRange = (typeof TIME_RANGES)[number];

const trafficTableData = [
  {
    service: "IP Transit 10G",
    avgIn: "4.2 Gbps",
    avgOut: "3.8 Gbps",
    peakIn: "8.7 Gbps",
    peakOut: "7.1 Gbps",
    p95: "7.8 Gbps",
  },
  {
    service: "DWDM 100G",
    avgIn: "62 Gbps",
    avgOut: "58 Gbps",
    peakIn: "95 Gbps",
    peakOut: "89 Gbps",
    p95: "91 Gbps",
  },
  {
    service: "Ethernet L2VPN 1G",
    avgIn: "450 Mbps",
    avgOut: "380 Mbps",
    peakIn: "890 Mbps",
    peakOut: "720 Mbps",
    p95: "810 Mbps",
  },
  {
    service: "Dark Fiber",
    avgIn: "N/A",
    avgOut: "N/A",
    peakIn: "N/A",
    peakOut: "N/A",
    p95: "N/A (unmanaged)",
  },
];

function TrafficAnalyticsView() {
  const [range, setRange] = useState<TimeRange>("24h");

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-semibold text-text-primary">Traffic Analytics</h2>
        <div className="flex items-center gap-1 p-1 rounded-xl" style={{ background: "var(--hover-bg)", border: "1px solid var(--card-border)" }}>
          {TIME_RANGES.map((r) => (
            <button
              key={r}
              onClick={() => setRange(r)}
              className="px-3 py-1.5 rounded-lg text-xs font-mono font-medium transition-all duration-200 cursor-pointer"
              style={{
                background: range === r ? "rgba(74,158,245,0.2)" : "transparent",
                color: range === r ? "#4A9EF5" : "var(--text-muted)",
                border: range === r ? "1px solid rgba(74,158,245,0.3)" : "1px solid transparent",
              }}
            >
              {r}
            </button>
          ))}
        </div>
      </div>

      {/* Main chart */}
      <Card>
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="text-sm font-medium text-text-secondary">Aggregate Traffic — All Services</div>
            <div className="text-xs text-text-muted font-mono mt-0.5">Simulated data · {range} view</div>
          </div>
          <div className="flex items-center gap-4 text-xs font-mono text-text-muted">
            <span
              className="flex items-center gap-1 px-2 py-0.5 rounded font-semibold tracking-wide"
              style={{ background: "rgba(0,212,170,0.1)", color: "#00d4aa", border: "1px solid rgba(0,212,170,0.2)" }}
            >
              Sample Data
            </span>
            <span className="flex items-center gap-1.5">
              <span className="w-3 h-0.5 bg-[#4A9EF5] inline-block rounded" />
              Ingress
            </span>
            <span className="flex items-center gap-1.5">
              <span className="w-3 h-0.5 bg-[#00d4aa] inline-block rounded" />
              Egress
            </span>
          </div>
        </div>
        <TrafficChart
          width={900}
          height={260}
          points={72}
          maxGbps={10}
          seed={range === "1h" ? 3 : range === "6h" ? 5 : range === "7d" ? 11 : range === "30d" ? 17 : 7}
        />
      </Card>

      {/* Per-service table */}
      <Card>
        <SectionTitle>Per-Service Breakdown</SectionTitle>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr>
                {["Service", "Avg In", "Avg Out", "Peak In", "Peak Out", "95th Percentile"].map(
                  (h) => (
                    <th
                      key={h}
                      className="text-left text-xs font-mono text-text-muted uppercase tracking-wider pb-3 pr-4"
                    >
                      {h}
                    </th>
                  )
                )}
              </tr>
            </thead>
            <tbody className="divide-y divide-divider">
              {trafficTableData.map((row) => (
                <tr key={row.service} className="group hover:bg-white/3 transition-colors">
                  <td className="py-3 pr-4 font-medium text-text-secondary">{row.service}</td>
                  <td className="py-3 pr-4 font-mono text-[#4A9EF5]">{row.avgIn}</td>
                  <td className="py-3 pr-4 font-mono text-[#00d4aa]">{row.avgOut}</td>
                  <td className="py-3 pr-4 font-mono text-text-tertiary">{row.peakIn}</td>
                  <td className="py-3 pr-4 font-mono text-text-tertiary">{row.peakOut}</td>
                  <td className="py-3 font-mono text-text-tertiary">{row.p95}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="mt-3 text-xs text-text-muted font-mono">
          All figures are simulated for demonstration purposes.
        </div>
      </Card>
    </div>
  );
}

// ─────────────────────────────────────────────
// SUPPORT TICKETS VIEW
// ─────────────────────────────────────────────

type TicketStatus = "open" | "resolved" | "closed";
type TicketPriority = "low" | "medium" | "high" | "critical";

const tickets: {
  id: number;
  subject: string;
  status: TicketStatus;
  priority: TicketPriority;
  updated: string;
}[] = [
  {
    id: 1847,
    subject: "BGP session timeout on Frankfurt",
    status: "resolved",
    priority: "medium",
    updated: "1 day ago",
  },
  {
    id: 1832,
    subject: "Request: Additional VLAN tags",
    status: "closed",
    priority: "low",
    updated: "5 days ago",
  },
  {
    id: 1819,
    subject: "Latency spike Istanbul route",
    status: "resolved",
    priority: "high",
    updated: "2 weeks ago",
  },
  {
    id: 1801,
    subject: "New service inquiry: Dubai PoP",
    status: "closed",
    priority: "low",
    updated: "3 weeks ago",
  },
];

const statusStyle: Record<TicketStatus, { label: string; bg: string; color: string }> = {
  open: { label: "Open", bg: "rgba(239,68,68,0.12)", color: "#ef4444" },
  resolved: { label: "Resolved", bg: "rgba(34,197,94,0.12)", color: "#22c55e" },
  closed: { label: "Closed", bg: "var(--hover-bg)", color: "var(--text-muted)" },
};

const priorityStyle: Record<TicketPriority, { label: string; color: string }> = {
  critical: { label: "Critical", color: "#ef4444" },
  high: { label: "High", color: "#f97316" },
  medium: { label: "Medium", color: "#eab308" },
  low: { label: "Low", color: "var(--text-muted)" },
};

function TicketsView() {
  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-semibold text-text-primary">Support Tickets</h2>
        <Link
          href="/contact"
          className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium transition-colors"
          style={{
            background: "rgba(74,158,245,0.12)",
            border: "1px solid rgba(74,158,245,0.25)",
            color: "#4A9EF5",
          }}
        >
          <Plus className="w-3.5 h-3.5" />
          New Ticket
        </Link>
      </div>

      <Card>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr>
                {["#", "Subject", "Status", "Priority", "Updated"].map((h) => (
                  <th
                    key={h}
                    className="text-left text-xs font-mono text-text-muted uppercase tracking-wider pb-3 pr-4"
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-divider">
              {tickets.map((t) => (
                <tr key={t.id} className="group hover:bg-white/3 transition-colors">
                  <td className="py-3.5 pr-4 font-mono text-text-muted text-xs">#{t.id}</td>
                  <td className="py-3.5 pr-4 font-medium text-text-secondary">{t.subject}</td>
                  <td className="py-3.5 pr-4">
                    <span
                      className="inline-flex items-center px-2.5 py-1 rounded-lg text-xs font-mono font-medium"
                      style={{
                        background: statusStyle[t.status].bg,
                        color: statusStyle[t.status].color,
                      }}
                    >
                      {statusStyle[t.status].label}
                    </span>
                  </td>
                  <td className="py-3.5 pr-4">
                    <span
                      className="text-xs font-mono font-medium"
                      style={{ color: priorityStyle[t.priority].color }}
                    >
                      {priorityStyle[t.priority].label}
                    </span>
                  </td>
                  <td className="py-3.5 text-xs text-text-muted font-mono">{t.updated}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Info */}
      <div
        className="flex items-start gap-3 rounded-xl p-4"
        style={{ background: "rgba(74,158,245,0.06)", border: "1px solid rgba(74,158,245,0.15)" }}
      >
        <Info className="w-4 h-4 text-[#4A9EF5] mt-0.5 shrink-0" />
        <div className="text-sm text-text-tertiary">
          For urgent issues, contact our 24/7 NOC at{" "}
          <a href="tel:+359 2 420 0300" className="text-[#4A9EF5] hover:underline">
            +359 2 420 0300
          </a>
          . Average response time for high-priority tickets: &lt;15 minutes.
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────
// BILLING VIEW
// ─────────────────────────────────────────────

const billingLines = [
  { label: "IP Transit 10G", amount: "€1,500/mo" },
  { label: "DWDM 100G Wavelength", amount: "€3,500/mo" },
  { label: "Ethernet L2VPN 1G", amount: "€500/mo" },
  { label: "Dark Fiber Pair", amount: "€600/mo" },
  { label: "Setup fees", amount: "€0" },
];

const addons = [
  { label: "BGP Communities add-on", amount: "€200/mo" },
  { label: "24/7 Premium Support", amount: "€350/mo" },
];

const invoices = [
  { id: "INV-2026-04", date: "April 1, 2026", amount: "€8,450", status: "Paid" },
  { id: "INV-2026-03", date: "March 1, 2026", amount: "€7,200", status: "Paid" },
  { id: "INV-2026-02", date: "February 1, 2026", amount: "€7,200", status: "Paid" },
];

function BillingView({ onToast }: { onToast: (msg: string) => void }) {
  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-semibold text-text-primary">Billing</h2>
        <div
          className="text-xs font-mono px-3 py-1.5 rounded-lg"
          style={{ background: "rgba(234,179,8,0.1)", color: "#eab308", border: "1px solid rgba(234,179,8,0.2)" }}
        >
          All figures are simulated
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        {/* Current month */}
        <Card>
          <SectionTitle>Current Month — April 2026</SectionTitle>
          <div className="space-y-2">
            {billingLines.map((line) => (
              <div key={line.label} className="flex items-center justify-between py-2 border-b border-divider">
                <span className="text-sm text-text-secondary">{line.label}</span>
                <span className="text-sm font-mono text-text-secondary">{line.amount}</span>
              </div>
            ))}
            <div className="flex items-center justify-between py-2 border-b border-divider">
              <span className="text-sm text-text-muted italic">Subtotal</span>
              <span className="text-sm font-mono text-text-tertiary">€6,100/mo</span>
            </div>
            {addons.map((a) => (
              <div key={a.label} className="flex items-center justify-between py-2 border-b border-divider">
                <span className="text-sm text-text-secondary">{a.label}</span>
                <span className="text-sm font-mono text-text-secondary">{a.amount}</span>
              </div>
            ))}
            <div
              className="flex items-center justify-between py-3 mt-1 px-3 rounded-xl"
              style={{ background: "rgba(74,158,245,0.08)", border: "1px solid rgba(74,158,245,0.15)" }}
            >
              <div className="flex items-center gap-2">
                <span className="font-semibold text-text-primary">Total</span>
                <span
                  className="text-[10px] font-mono font-semibold px-1.5 py-0.5 rounded tracking-wide"
                  style={{ background: "rgba(0,212,170,0.12)", color: "#00d4aa", border: "1px solid rgba(0,212,170,0.2)" }}
                >
                  Sample Data
                </span>
              </div>
              <span className="font-bold font-mono text-[#4A9EF5] text-lg">€8,450/mo</span>
            </div>
            <div className="text-xs text-text-muted font-mono mt-2">
              * Simulated billing data for demo purposes only.
            </div>
          </div>
        </Card>

        {/* Invoice history */}
        <Card>
          <div className="flex items-center justify-between mb-4">
            <span className="text-xs font-mono tracking-wider uppercase text-text-muted">
              Invoice History
            </span>
            <span
              className="text-[10px] font-mono font-semibold px-1.5 py-0.5 rounded tracking-wide"
              style={{ background: "rgba(0,212,170,0.1)", color: "#00d4aa", border: "1px solid rgba(0,212,170,0.2)" }}
            >
              Sample Data
            </span>
          </div>
          <div className="space-y-2">
            {invoices.map((inv) => (
              <div
                key={inv.id}
                className="flex items-center justify-between py-3 border-b border-divider last:border-0"
              >
                <div>
                  <div className="text-sm font-mono text-text-secondary">{inv.id}</div>
                  <div className="text-xs text-text-muted mt-0.5">{inv.date}</div>
                </div>
                <div className="flex items-center gap-3">
                  <span className="text-sm font-mono text-text-secondary">{inv.amount}</span>
                  <span
                    className="text-xs font-mono px-2.5 py-1 rounded-lg"
                    style={{ background: "rgba(34,197,94,0.12)", color: "#22c55e" }}
                  >
                    {inv.status}
                  </span>
                  <button
                    onClick={() => onToast("Invoice download available in production portal")}
                    className="w-7 h-7 rounded-lg flex items-center justify-center transition-colors hover:bg-hover-bg-strong cursor-pointer"
                    title="Download invoice"
                  >
                    <Download className="w-3.5 h-3.5 text-text-muted" />
                  </button>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-4 pt-4 border-t border-divider">
            <div className="text-xs text-text-muted font-mono">Payment method on file</div>
            <div className="text-sm text-text-tertiary mt-1.5 flex items-center gap-2">
              <CreditCard className="w-4 h-4 text-text-muted" />
              Bank Transfer — IBAN ending in ...4821
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────
// API KEYS VIEW
// ─────────────────────────────────────────────

type ApiKeyStatus = "active" | "revoked";

const apiKeys: {
  name: string;
  created: string;
  status: ApiKeyStatus;
  lastUsed: string;
}[] = [
  { name: "prod-monitoring-v2", created: "Apr 13, 2026", status: "active", lastUsed: "2 hours ago" },
  { name: "staging-test", created: "Mar 1, 2026", status: "active", lastUsed: "4 days ago" },
  { name: "old-integration", created: "Jan 15, 2026", status: "revoked", lastUsed: "45 days ago" },
];

const codeExample = `curl -H "Authorization: Bearer sc_live_xxxxx" \\
  https://api.sofia-connect.net/v1/services`;

function ApiKeysView({ onToast }: { onToast: (msg: string) => void }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    navigator.clipboard.writeText(codeExample).catch(() => {});
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-semibold text-text-primary">API Keys</h2>
        <button
          onClick={() => onToast("API key creation available in production portal")}
          className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium transition-colors cursor-pointer"
          style={{
            background: "rgba(74,158,245,0.12)",
            border: "1px solid rgba(74,158,245,0.25)",
            color: "#4A9EF5",
          }}
        >
          <Plus className="w-3.5 h-3.5" />
          Create New Key
        </button>
      </div>

      <Card>
        <SectionTitle>Active Keys</SectionTitle>
        <div className="space-y-3">
          {apiKeys.map((key) => (
            <div
              key={key.name}
              className="flex items-center justify-between p-4 rounded-xl"
              style={{
                background: "var(--hover-bg)",
                border: "1px solid var(--divider)",
                opacity: key.status === "revoked" ? 0.55 : 1,
              }}
            >
              <div className="flex items-center gap-3">
                <div
                  className="w-8 h-8 rounded-lg flex items-center justify-center"
                  style={{ background: key.status === "active" ? "rgba(74,158,245,0.12)" : "var(--hover-bg)" }}
                >
                  <Key className="w-3.5 h-3.5" style={{ color: key.status === "active" ? "#4A9EF5" : "var(--text-muted)" }} />
                </div>
                <div>
                  <div className="text-sm font-mono font-medium text-text-secondary">{key.name}</div>
                  <div className="text-xs text-text-muted font-mono mt-0.5">
                    Created {key.created} · Last used {key.lastUsed}
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <span
                  className="text-xs font-mono px-2.5 py-1 rounded-lg"
                  style={
                    key.status === "active"
                      ? { background: "rgba(34,197,94,0.12)", color: "#22c55e" }
                      : { background: "rgba(239,68,68,0.1)", color: "#ef4444" }
                  }
                >
                  {key.status === "active" ? "Active" : "Revoked"}
                </span>
                {key.status === "active" && (
                  <button
                    onClick={() => onToast("Key revocation available in production portal")}
                    className="text-xs text-text-muted hover:text-red-400 transition-colors cursor-pointer font-mono"
                  >
                    Revoke
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      </Card>

      {/* Code example */}
      <Card>
        <SectionTitle>API Usage Example</SectionTitle>
        <div
          className="relative rounded-xl overflow-hidden"
          style={{ background: "var(--glass-bg)", border: "1px solid var(--card-border)" }}
        >
          <div className="flex items-center justify-between px-4 py-2 border-b border-divider">
            <span className="text-xs font-mono text-text-muted">bash</span>
            <button
              onClick={handleCopy}
              className="flex items-center gap-1.5 text-xs text-text-muted hover:text-text-secondary transition-colors cursor-pointer"
            >
              <Copy className="w-3 h-3" />
              {copied ? "Copied!" : "Copy"}
            </button>
          </div>
          <pre className="p-4 text-sm font-mono text-[#4A9EF5] overflow-x-auto leading-relaxed">
            {codeExample}
          </pre>
        </div>

        <div className="mt-5 space-y-3">
          <div className="text-sm font-medium text-text-tertiary">API Documentation</div>
          <div className="flex flex-wrap gap-2">
            <Link
              href="/developers"
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-mono transition-colors"
              style={{ background: "rgba(74,158,245,0.08)", border: "1px solid rgba(74,158,245,0.15)", color: "#4A9EF5" }}
            >
              Developer Portal
              <ExternalLink className="w-3 h-3" />
            </Link>
            <a
              href="https://api.sofia-connect.net/v1/docs"
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-mono transition-colors"
              style={{ background: "var(--hover-bg)", border: "1px solid var(--card-border)", color: "var(--text-muted)" }}
              target="_blank"
              rel="noopener noreferrer"
            >
              API Reference
              <ExternalLink className="w-3 h-3" />
            </a>
          </div>
        </div>
      </Card>

      <div
        className="flex items-start gap-3 rounded-xl p-4"
        style={{ background: "rgba(74,158,245,0.06)", border: "1px solid rgba(74,158,245,0.15)" }}
      >
        <Shield className="w-4 h-4 text-[#4A9EF5] mt-0.5 shrink-0" />
        <div className="text-sm text-text-tertiary">
          API keys grant programmatic access to your Sofia Connect services. Keep keys secret and rotate them regularly. 
          All API activity is logged and auditable in the production portal.
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────
// MAIN PORTAL CLIENT COMPONENT
// ─────────────────────────────────────────────

export function PortalDemoClient() {
  const [activeView, setActiveView] = useState<View>("dashboard");
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [bannerVisible, setBannerVisible] = useState(true);
  const { toasts, addToast } = useToast();

  // Close sidebar when view changes on mobile
  const handleNavClick = (view: View) => {
    setActiveView(view);
    setSidebarOpen(false);
  };

  return (
    <div className="min-h-screen bg-background flex flex-col" style={{ paddingTop: "64px" }}>
      {/*
        Page-level H1 for the Client Portal preview. Kept visually hidden
        because the portal chrome (sidebar, breadcrumb) already names the
        current view. Each view then uses h2 for its section title, so the
        outline is h1 (page) -> h2 (view) -> h3+ (widgets).
      */}
      <h1 className="sr-only">Client Portal</h1>
      {/* Demo Mode Banner */}
      <AnimatePresence>
        {bannerVisible && (
          <motion.div
            initial={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="sticky top-16 z-40 overflow-hidden shrink-0"
            style={{
              background: "rgba(0,212,170,0.08)",
              borderBottom: "1px solid rgba(0,212,170,0.2)",
            }}
          >
            <div className="max-w-full px-4 sm:px-6 lg:px-8 py-3 flex items-center gap-3 flex-wrap">
              <div
                className="flex items-center gap-1.5 px-2.5 py-0.5 rounded-md text-xs font-mono font-semibold tracking-wider uppercase shrink-0"
                style={{ background: "rgba(0,212,170,0.18)", color: "#00d4aa" }}
              >
                <span className="w-1.5 h-1.5 rounded-full bg-[#00d4aa] animate-pulse" />
                Demo Mode
              </div>
              <p className="text-sm text-text-tertiary flex-1 min-w-0">
                Explore the portal with sample data.{" "}
                <span className="hidden sm:inline">No login required.</span>
              </p>
              <div className="flex items-center gap-3 shrink-0">
                <Link
                  href="/contact"
                  className="flex items-center gap-1.5 px-3.5 py-1.5 rounded-lg text-xs font-medium transition-all hover:opacity-90"
                  style={{ background: "#00d4aa", color: "#000" }}
                >
                  Ready for the real thing? Contact Sales
                  <ArrowUpRight className="w-3 h-3" />
                </Link>
                <button
                  onClick={() => setBannerVisible(false)}
                  className="w-6 h-6 flex items-center justify-center rounded-lg text-text-muted hover:text-text-secondary hover:bg-hover-bg-strong transition-colors cursor-pointer"
                  aria-label="Dismiss demo banner"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Portal layout */}
      <div className="flex flex-1 overflow-hidden relative">
        {/* Mobile overlay */}
        <AnimatePresence>
          {sidebarOpen && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 z-30 bg-background/60 lg:hidden"
              style={{ top: "64px" }}
              onClick={() => setSidebarOpen(false)}
            />
          )}
        </AnimatePresence>

        {/* Sidebar */}
        <motion.aside
          initial={false}
          animate={{ x: sidebarOpen ? 0 : "-100%" }}
          transition={{ type: "spring", stiffness: 300, damping: 30 }}
          className="fixed left-0 bottom-0 z-40 w-60 flex flex-col lg:translate-x-0 lg:static lg:z-auto"
          style={{
            top: "64px",
            background: "rgba(4,4,8,0.97)",
            borderRight: "1px solid var(--divider)",
            backdropFilter: "blur(16px)",
          }}
        >
          {/* Logo area */}
          <div
            className="flex items-center gap-2.5 px-5 py-5 border-b"
            style={{ borderColor: "var(--divider)" }}
          >
            <SofiaConnectLogo className="w-7 h-7 text-[#4A9EF5]" aria-hidden />
            <div>
              <div className="text-sm font-semibold text-text-primary">Sofia Connect</div>
              <div className="text-[10px] font-mono text-text-muted tracking-wider uppercase">
                Client Portal
              </div>
            </div>
          </div>

          {/* Nav items */}
          <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
            {NAV_ITEMS.map(({ id, label, Icon }) => {
              const isActive = activeView === id;
              return (
                <button
                  key={id}
                  onClick={() => handleNavClick(id)}
                  className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-all duration-200 text-left cursor-pointer group"
                  style={{
                    background: isActive ? "rgba(74,158,245,0.12)" : "transparent",
                    color: isActive ? "#4A9EF5" : "var(--text-muted)",
                    border: isActive ? "1px solid rgba(74,158,245,0.2)" : "1px solid transparent",
                  }}
                >
                  <Icon
                    className="w-4 h-4 shrink-0 transition-colors"
                    color={isActive ? "#4A9EF5" : "var(--text-muted)"}
                  />
                  {label}
                  {isActive && (
                    <motion.div
                      layoutId="activeIndicator"
                      className="ml-auto w-1.5 h-1.5 rounded-full bg-[#4A9EF5]"
                    />
                  )}
                </button>
              );
            })}
          </nav>

          {/* Sidebar footer */}
          <div
            className="px-4 py-4 border-t"
            style={{ borderColor: "var(--divider)" }}
          >
            <div
              className="flex items-center gap-2.5 px-3 py-2.5 rounded-xl"
              style={{ background: "var(--hover-bg)" }}
            >
              <div
                className="w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold"
                style={{ background: "rgba(74,158,245,0.2)", color: "#4A9EF5" }}
              >
                DC
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-xs font-medium text-text-secondary truncate">Demo Client</div>
                <div className="text-[10px] font-mono text-text-muted truncate">
                  demo@example.com
                </div>
              </div>
            </div>
          </div>
        </motion.aside>

        {/* Main content */}
        <div className="flex-1 flex flex-col min-w-0 overflow-auto">
          {/* Top bar (mobile hamburger + breadcrumb) */}
          <div
            className="flex items-center gap-3 px-4 sm:px-6 py-3 border-b lg:px-8"
            style={{
              borderColor: "var(--divider)",
              background: "var(--glass-bg)",
              backdropFilter: "blur(12px)",
            }}
          >
            {/* Hamburger (mobile only) */}
            <button
              onClick={() => setSidebarOpen(!sidebarOpen)}
              className="lg:hidden w-8 h-8 flex items-center justify-center rounded-lg transition-colors hover:bg-hover-bg-strong cursor-pointer"
              aria-label="Toggle sidebar"
            >
              {sidebarOpen ? (
                <X className="w-4 h-4 text-text-tertiary" />
              ) : (
                <Menu className="w-4 h-4 text-text-tertiary" />
              )}
            </button>

            {/* Breadcrumb */}
            <div className="flex items-center gap-1.5 text-sm text-text-muted font-mono">
              <span>Portal</span>
              <ChevronRight className="w-3 h-3" />
              <span className="text-text-secondary capitalize">
                {NAV_ITEMS.find((n) => n.id === activeView)?.label}
              </span>
            </div>

            <div className="ml-auto flex items-center gap-2">
              <button
                onClick={() => addToast("Data refreshed — all figures are simulated")}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-mono text-text-muted hover:text-text-tertiary transition-colors cursor-pointer"
                style={{ border: "1px solid var(--divider)" }}
              >
                <RefreshCw className="w-3 h-3" />
                <span className="hidden sm:inline">Refresh</span>
              </button>
              <div
                className="hidden sm:flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-mono"
                style={{ background: "rgba(34,197,94,0.08)", color: "#22c55e", border: "1px solid rgba(34,197,94,0.15)" }}
              >
                <CheckCircle2 className="w-3 h-3" />
                All systems operational
              </div>
            </div>
          </div>

          {/* Page content */}
          <div className="flex-1 px-4 sm:px-6 lg:px-8 py-6">
            <AnimatePresence mode="wait">
              <motion.div
                key={activeView}
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -8 }}
                transition={{ duration: 0.22 }}
              >
                {activeView === "dashboard" && <DashboardView onToast={addToast} />}
                {activeView === "services" && <ServicesView onToast={addToast} />}
                {activeView === "traffic" && <TrafficAnalyticsView />}
                {activeView === "tickets" && <TicketsView />}
                {activeView === "billing" && <BillingView onToast={addToast} />}
                {activeView === "apikeys" && <ApiKeysView onToast={addToast} />}
              </motion.div>
            </AnimatePresence>

            {/* Footer note */}
            <div className="mt-8 pt-6 border-t border-divider text-center">
              <p className="text-xs text-text-muted font-mono">
                Sofia Connect Client Portal Preview · All data is simulated · Not connected to production systems
              </p>
            </div>
          </div>
        </div>
      </div>

      <ToastContainer toasts={toasts} />
    </div>
  );
}
